import cn from 'classnames';
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { propTypes as authPropTypes, defaultProps as authDefaultProps } from 'shared/proptypes/Auth';
import Avatar from 'shared/components/Avatar';
import Badge from 'shared/components/Badge';
import IconBadge from 'shared/components/IconBadge';
import Logo from 'shared/components/Logo';
import LanguageSwitch from 'shared/components/LanguageSwitch';
import { userportal as routes } from 'shared/routes';
import { ALL_JOBS_LINK } from 'shared/constants';
import styles from './Desktop.module.scss';

export const Desktop = ({
  loggedIn,
  user,
  logout,
  unreadApplicationChangesCount,
  unreadOfferChangesCount,
  hasInsufficientProfile,
  onChangeLanguage,
  language,
  noBottomPadding
}) => {
  const { t } = useTranslation('userportal');

  return (
    <div className={styles.desktop}>
      <div className="container">
        <div className="row">
          <div className={cn('col-12', styles.navigation, { [styles.noBottomPadding]: noBottomPadding })}>

            <div className={styles.logoCol}>
              <Link to={routes.root} className={styles.logo}>
                <Logo />
              </Link>
              <div className={styles.name}>
                <strong><Link to={routes.root}>{t('common:name')}</Link></strong>
                <p><Link to={routes.root}>{t('common:motto')}</Link></p>
              </div>
            </div>

            <nav className={styles.menuCol}>
              <ul className={styles.menu}>
                <li>
                  <Link to={ALL_JOBS_LINK} rel="nofollow">
                    <i className="la la-list-ol" />
                    {t('navigation.allJobs')}
                  </Link>
                </li>

                {!loggedIn && (
                  <>
                    <li>
                      <Link to={routes.login}>
                        <i className="la la-key" />
                        {t('navigation.login')}
                      </Link>
                    </li>
                    <li>
                      <Link to={routes.signup} className={styles.signup}>
                        <i className="la la-user-plus" />
                        {t('navigation.signup')}
                      </Link>
                    </li>
                  </>
                )}

                {loggedIn && (
                  <>
                    <li>
                      <Link to={routes.user.favoriteJobs}>
                        <i className="lar la-heart" />
                        {t('navigation.favoriteJobs')}
                      </Link>
                    </li>
                    <li className={unreadOfferChangesCount > 0 ? styles.hasBadge : null}>
                      <Link to={routes.user.offers}>
                        <i className="las la-sms" />
                        {unreadOfferChangesCount > 0 ? (
                          <Badge highlighted content={unreadOfferChangesCount}>
                            {t('navigation.myOffers')}
                          </Badge>
                        ) : (<span>{t('navigation.myOffers')}</span>)}
                      </Link>
                    </li>
                    <li className={unreadApplicationChangesCount > 0 ? styles.hasBadge : null}>
                      <Link to={routes.user.applications}>
                        <i className="la la-clipboard-check" />
                        {unreadApplicationChangesCount > 0 ? (
                          <Badge highlighted content={unreadApplicationChangesCount}>
                            {t('navigation.myPositions')}
                          </Badge>
                        ) : t('navigation.myPositions')}
                      </Link>
                    </li>
                    {/* <li className={hasInsufficientProfile ? styles.hasBadge : null}>
                      <Link to={routes.user.profile.view} className="relative">
                        <span>{t('navigation.myProfile')}</span>
                        {hasInsufficientProfile && <IconBadge tooltipPosition="bottom">{t('navigation.insufficientProfile')}</IconBadge>}
                      </Link>
                    </li> */}
                    <li>
                      <Link to={routes.user.profile.view} className="relative">
                        <Avatar user={user} transparent />
                        {hasInsufficientProfile && (
                          <IconBadge
                            className={styles.warnBadge}
                            tooltipPosition="bottom"
                          >
                            {t('navigation.insufficientProfile')}
                          </IconBadge>
                        )}
                      </Link>
                    </li>
                    <li>
                      <Link to={routes.root} onClick={logout}>
                        <i className="la la-sign-out-alt" />
                        {t('navigation.logout')}
                      </Link>
                    </li>
                  </>
                )}
                <li className={styles.lang}>
                  <LanguageSwitch language={language} onChange={onChangeLanguage} />
                </li>
              </ul>
            </nav>

          </div>
        </div>
      </div>
    </div>
  );
};

Desktop.propTypes = {
  loggedIn: authPropTypes.loggedIn,
  user: authPropTypes.user,
  logout: PropTypes.func,
  unreadApplicationChangesCount: PropTypes.number,
  unreadOfferChangesCount: PropTypes.number,
  hasInsufficientProfile: PropTypes.bool,
  onChangeLanguage: PropTypes.func,
  language: authPropTypes.language,
  noBottomPadding: PropTypes.bool,
};
Desktop.defaultProps = {
  loggedIn: false,
  user: authDefaultProps.user,
  logout() {},
  unreadApplicationChangesCount: 0,
  unreadOfferChangesCount: 0,
  hasInsufficientProfile: false,
  onChangeLanguage() {},
  language: authDefaultProps.language,
  noBottomPadding: false,
};

export default Desktop;
